import { useEffect, useState } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import FeatherIcon from 'feather-icons-react'

import start from '../src/mario1.png'
import applestore from '../src/get_from_app_store_se.svg'
import trustpilot from '../src/trustpilot-4.png'
import login from '../src/kronos-new.png'
import googleplay from '../src/google-play.png'
import chat from '../src/chat-2.png'
import kronogram from '../src/kronos.png'
import vintage from '../src/ImVintageLogo-1.png'
import hwj from '../src/hwj2.png'
import diamondInvest from '../src/diamond-invest.jpeg'
import careofcarl from '../src/careofcarl.svg'
import urbrev from '../src/urbrev-logo.png'
import cc from '../src/cc.jpeg'
import svklockvarderingLogo from '../src/värdering.png'
import ipw from '../src/impressive-watches.webp'
import jcwatches from '../src/jcwathes.png'
import ppwatches from '../src/PPWatches3_cropped.png'
import klocksalongen from '../src/klocksalongen.avif'
import luxurywathes from '../src/luxurywatches-logo.svg'
import watchvault from '../src/watchvault-2.webp'
import Chronos from '../src/777chronos.png'
import scandchrono from '../src/scand-chrono-logo.png'

const navigation = [
  { name: 'Kronos köpskydd', href: '/kronos-secure' },
  { name: 'Kronos Certified', href: '/kronos-certified' },
  { name: 'Kontrollera certifiering', href: '/kronos-certified-control' },
  { name: 'Klockvärdering', href: '/klockvardering' },
  { name: 'Företagsannonsering', href: '/foretagsannonsering' },
  { name: 'Marknadsföring', href: '/marknadsforing' },
  { name: 'Blogg', href: '/blogg' },
  { name: 'Integritetspolicy', href: '/integritetspolicy' },
  { name: 'Användarvillkor', href: '/anvandarvillkor' },
]

const advertisers = [
  { name: 'Luxury Watches', logo: luxurywathes, url: 'https://www.luxurywatches.se' },
  { name: 'Care of Carl', logo: careofcarl, url: 'https://www.careofcarl.se' },
  { name: 'HWJ', logo: hwj, url: 'https://hwj.se' },
  { name: 'Impressive Watches', logo: ipw, url: 'https://impressivewatches.com' },
  { name: 'Klocksalongen', logo: klocksalongen, url: 'https://www.klocksalongen.se' },
  { name: '777 Chronos', logo: Chronos, url: 'https://777chronos.com/' },
  { name: 'PP Watches', logo: ppwatches, url: 'https://www.ppwatches.se' },
  { name: 'JC Watches', logo: jcwatches, url: 'https://www.jcwatches.se' },
  { name: 'Crown Collector', logo: cc, url: 'https://crown-collector.com' },
  { name: 'Vintage Watches', logo: vintage, url: 'https://www.vintagewatches.se' },
  {
    name: 'Diamond Invest',
    logo: diamondInvest,
    url: 'https://www.instagram.com/diamondinvest_di/',
  },
  { name: 'Watchvault', logo: watchvault, url: 'https://www.watchvault.se' },
  { name: 'Watchvault', logo: scandchrono, url: 'https://scandinavianchrono.se' },
]

const partners = [
  { name: 'UrBrev', logo: urbrev, url: 'https://www.urbrev.se' },
  {
    name: 'Svensk Klockvärdering',
    logo: svklockvarderingLogo,
    url: 'https://www.svenskklockvardering.se',
  },
]

function StartPage({ siteLogo }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Client-side SEO metadata. For strongest indexing, render this metadata and
  // the page content on the server or pre-render the route.
  useEffect(() => {
    const title = 'Köpa och sälja klockor | Begagnade klockor på Kronos'
    const description =
      'Köp och sälj begagnade klockor på Kronos. Upptäck klockor från bland annat Rolex och Omega, chatta direkt med säljare och läs om Kronos Certified och Kronos Secure.'
    const canonicalUrl = 'https://www.kronos.se/'

    document.title = title

    const setMeta = (selector, attributes) => {
      let element = document.head.querySelector(selector)
      if (!element) {
        element = document.createElement('meta')
        document.head.appendChild(element)
      }
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value))
    }

    setMeta('meta[name="description"]', { name: 'description', content: description })
    setMeta('meta[name="robots"]', {
      name: 'robots',
      content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    })
    setMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' })
    setMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'sv_SE' })
    setMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'Kronos' })
    setMeta('meta[property="og:title"]', { property: 'og:title', content: title })
    setMeta('meta[property="og:description"]', { property: 'og:description', content: description })
    setMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl })
    setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' })
    setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title })
    setMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: description,
    })

    let canonical = document.head.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', canonicalUrl)

    const structuredData = [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Kronos',
        url: canonicalUrl,
        logo: 'https://www.kronos.se/favicon.ico',
        sameAs: [
          'https://www.instagram.com/kronosappen/',
          'https://www.facebook.com/profile.php?id=61573060364645',
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Kronos',
        url: canonicalUrl,
        inLanguage: 'sv-SE',
        description,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Vad är Kronos?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Kronos är en svensk marknadsplats för klockor där användare kan upptäcka annonser, köpa och sälja klockor samt kommunicera direkt i appen.',
            },
          },
          {
            '@type': 'Question',
            name: 'Kan jag köpa begagnade klockor på Kronos?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Kronos samlar klockannonser från privatpersoner och handlare. Läs annonsen, jämför informationen och kontakta säljaren om du vill veta mer om en specifik klocka.',
            },
          },
          {
            '@type': 'Question',
            name: 'Hur kan jag göra en klockaffär tryggare?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Läs om Kronos Secure köpskydd och Kronos Certified för att förstå vilka tilläggstjänster som finns och vad de omfattar.',
            },
          },
          {
            '@type': 'Question',
            name: 'Hur säljer jag min klocka?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Ladda ner Kronos, skapa en annons med tydliga bilder och relevant information och svara på frågor från intresserade köpare.',
            },
          },
          {
            '@type': 'Question',
            name: 'Var kan jag få min klocka värderad?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Kronos har en sida om Klockvärdering med information om värdering av klockor.',
            },
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'MobileApplication',
        name: 'Kronos',
        applicationCategory: 'ShoppingApplication',
        operatingSystem: 'iOS, Android',
        inLanguage: 'sv-SE',
        description:
          'En svensk marknadsplats där användare kan köpa och sälja klockor och kommunicera direkt i appen.',
        url: canonicalUrl,
        downloadUrl: [
          'https://apps.apple.com/se/app/chronos/id6472299482',
          'https://play.google.com/store/apps/details?id=com.stagem.Chronos',
        ],
      },
    ]

    let schemaScript = document.getElementById('kronos-homepage-schema')
    if (!schemaScript) {
      schemaScript = document.createElement('script')
      schemaScript.id = 'kronos-homepage-schema'
      schemaScript.type = 'application/ld+json'
      document.head.appendChild(schemaScript)
    }
    schemaScript.textContent = JSON.stringify(structuredData)
  }, [])

  return (
    <div className="min-h-screen overflow-hidden bg-white text-slate-900">
      <header className="fixed inset-x-0 top-0 z-50 bg-white shadow">
        <nav className="flex items-center justify-between p-6 lg:px-8">
          <a href="/" className="flex items-center -m-1.5 p-1.5">
            <span className="sr-only">Krono</span>
            <img alt="Krono Logo" src={siteLogo} className="h-6 w-auto rounded-md" />
          </a>

          <div className="flex lg:hidden">
            <button
              type="button"
              className="p-2.5 text-gray-700 rounded-md"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Open menu</span>
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                />
              </svg>
            </button>
          </div>

          <div className="hidden lg:flex lg:gap-x-8">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-sm font-semibold text-gray-900 hover:underline"
              >
                {item.name}
              </a>
            ))}
          </div>
        </nav>

        {/* Mobile menu */}
        <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
          <div className="fixed inset-0 z-50" />
          <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-white px-6 py-6 overflow-y-auto shadow-lg">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="p-2.5 text-gray-700 rounded-md"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-6 space-y-2">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                >
                  {item.name}
                </a>
              ))}
            </div>
          </DialogPanel>
        </Dialog>
      </header>

      <main>
        <section className="relative isolate">
          <div className="absolute inset-0 -z-10 bg-white" />
          <div className="absolute -right-32 top-10 -z-10 h-96 w-96 rounded-full bg-sky-200/40 blur-3xl" />
          <div className="absolute -left-32 top-56 -z-10 h-80 w-80 rounded-full bg-indigo-100/60 blur-3xl" />
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-16 pt-14 sm:px-8 sm:pt-20 lg:grid-cols-[1.02fr_0.98fr] lg:gap-8 lg:px-8 lg:pb-24 lg:pt-20">
            <div className="relative z-10 mx-auto max-w-2xl text-center lg:mx-0 lg:text-left">
              <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white/80 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-blue-800 shadow-sm sm:text-sm">
                Sveriges marknadsplats för klockor
              </div>
              <h1 className="font-poppins text-5xl font-semibold tracking-tight text-slate-950 sm:text-6xl lg:text-7xl">
                Köp och sälj klockor
                <br />
                <span className="bg-gradient-to-r from-blue-700 to-sky-500 bg-clip-text text-transparent">
                  på Kronos.
                </span>
              </h1>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-slate-600 sm:text-xl lg:mx-0">
                Upptäck begagnade klockor och sälj din egen på en svensk marknadsplats för
                klockälskare. Hitta modeller från märken som Rolex och Omega och läs om våra
                tjänster för tryggare klockaffärer.
              </p>

              <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center lg:justify-start">
                <a
                  href="https://se.trustpilot.com/review/kronosapp.se"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg bg-white p-2 shadow-sm ring-1 ring-slate-200/70"
                >
                  <img src={trustpilot} alt="Omdömen på Trustpilot" className="h-8 w-auto" />
                </a>
                <span className="text-sm text-slate-500">
                  En plats för klockälskare och samlare
                </span>
              </div>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
                <a
                  href="https://apps.apple.com/se/app/chronos/id6472299482"
                  aria-label="Ladda ner från App Store"
                >
                  <img src={applestore} alt="Ladda ner på App Store" className="h-11 w-auto" />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.stagem.Chronos"
                  aria-label="Hämta på Google Play"
                >
                  <img src={googleplay} alt="Hämta på Google Play" className="h-11 w-auto" />
                </a>
              </div>
              <div className="mt-9 flex items-center justify-center gap-3 lg:justify-start">
                <span className="text-sm font-medium text-slate-500">Följ Kronos</span>
                <a
                  href="https://www.facebook.com/profile.php?id=61573060364645"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-700 transition hover:bg-blue-600 hover:text-white"
                >
                  <FeatherIcon icon="facebook" size={17} />
                </a>
                <a
                  href="https://www.instagram.com/kronosappen/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-700 transition hover:bg-pink-600 hover:text-white"
                >
                  <FeatherIcon icon="instagram" size={17} />
                </a>
              </div>
            </div>
            <div className="relative mx-auto w-full max-w-xl lg:max-w-none">
              <div className="absolute -inset-5 rounded-[2.5rem] bg-gradient-to-br from-blue-200/60 via-sky-100/40 to-indigo-100/50 blur-2xl" />
              <div className="relative overflow-hidden rounded-[2rem] border border-white/80 bg-white/80 p-3 shadow-2xl shadow-blue-950/10 backdrop-blur-sm sm:p-4">
                <div className="absolute left-7 top-7 z-10 rounded-full border border-white/70 bg-white/90 px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm backdrop-blur">
                  Klockor för varje stil
                </div>
                <img
                  src={login}
                  alt="Skärmbild från Kronos app för att köpa och sälja klockor"
                  className="mx-auto max-h-[650px] w-full rounded-[1.5rem] object-contain object-top"
                />
                <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/80 bg-white/95 p-4 shadow-xl backdrop-blur sm:bottom-7 sm:left-7 sm:right-7 sm:p-5">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-blue-700">
                    Kronos
                  </p>
                  <p className="mt-1 text-base font-semibold text-slate-950 sm:text-lg">
                    Upptäck. Jämför. Hitta din nästa favorit.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-slate-200 bg-white py-14">
          <div className="mx-auto max-w-7xl px-5 text-center sm:px-8 lg:px-8">
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-700">
              Våra samarbetspartners
            </p>
            <h2 className="mt-3 font-poppins text-3xl font-semibold tracking-tight text-slate-950">
              Tillsammans för tryggare affärer
            </h2>
            <div className="mx-auto mt-8 grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
              {partners.map((item) => (
                <a
                  key={item.name}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-32 items-center justify-center rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition hover:border-blue-200 hover:shadow-md"
                >
                  <img
                    src={item.logo}
                    alt={item.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </a>
              ))}
            </div>
          </div>
        </section>

        <section
          id="sa-fungerar-kronos"
          aria-labelledby="benefits-heading"
          className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-8 lg:py-24 border-t border-slate-200"
        >
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-700">
              Mer än en marknadsplats
            </p>
            <h2
              id="benefits-heading"
              className="mt-4 font-poppins text-3xl font-semibold tracking-tight text-slate-950 sm:text-5xl"
            >
              En marknadsplats för begagnade klockor
            </h2>
            <p className="mt-5 text-base leading-7 text-slate-600 sm:text-lg">
              Hitta rätt klocka, prata direkt med säljaren och få tillgång till tjänster som hjälper
              dig att känna dig tryggare genom affären.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            <article className="group overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-950/5">
              <div className="border-b border-slate-100 bg-gradient-to-b from-blue-50 to-white px-6 pt-7">
                <h3 className="text-lg font-semibold text-slate-950">Hitta din nästa klocka</h3>
                <p className="mt-2 min-h-12 text-sm leading-6 text-slate-600">
                  Sök och filtrera bland klockor för olika stilar, samlingar och budgetar.
                </p>
              </div>
              <div className="px-4 pb-4 pt-5">
                <img
                  src={start}
                  alt="Sök och filtrera bland klockannonser i Kronos app"
                  className="mx-auto max-h-[390px] w-full rounded-2xl object-contain object-top transition duration-500 group-hover:scale-[1.02]"
                />
              </div>
            </article>
            <article className="group overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-950/5">
              <div className="border-b border-slate-100 bg-gradient-to-b from-blue-50 to-white px-6 pt-7">
                <h3 className="text-lg font-semibold text-slate-950">Prata direkt i appen</h3>
                <p className="mt-2 min-h-12 text-sm leading-6 text-slate-600">
                  Ställ frågor, diskutera detaljer och håll kontakten med andra klockintresserade.
                </p>
              </div>
              <div className="px-4 pb-4 pt-5">
                <img
                  src={chat}
                  alt="Chatta direkt med köpare och säljare i Kronos app"
                  className="mx-auto max-h-[390px] w-full rounded-2xl object-contain object-top transition duration-500 group-hover:scale-[1.02]"
                />
              </div>
            </article>
            <article className="group overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-950/5">
              <div className="border-b border-slate-100 bg-gradient-to-b from-blue-50 to-white px-6 pt-7">
                <h3 className="text-lg font-semibold text-slate-950">Dela din passion</h3>
                <p className="mt-2 min-h-12 text-sm leading-6 text-slate-600">
                  Visa upp din samling och inspireras av andra i ett community för klockälskare.
                </p>
              </div>
              <div className="px-4 pb-4 pt-5">
                <img
                  src={kronogram}
                  alt="Dela bilder på klockor och inspireras av andra klockentusiaster i Kronos"
                  className="mx-auto max-h-[390px] w-full rounded-2xl object-contain object-top transition duration-500 group-hover:scale-[1.02]"
                />
              </div>
            </article>
          </div>
        </section>

        <section aria-labelledby="advertisers-heading" className="bg-slate-50 py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-8">
            <div className="mb-10 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-700">
                  Handlare på Kronos
                </p>
                <h2
                  id="advertisers-heading"
                  className="mt-3 font-poppins text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl"
                >
                  Klockhandlare och annonsörer på Kronos
                </h2>
                <p className="mt-3 max-w-2xl text-slate-600">
                  Möt klockhandlare och specialister som valt att synas där klockintresset finns.
                </p>
              </div>
              <Link
                to="/foretagsannonsering"
                className="inline-flex w-fit items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-blue-200 hover:text-blue-700"
              >
                Annonsera på Kronos <span aria-hidden="true">↗</span>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
              {advertisers.map((item) => (
                <a
                  key={item.name}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={item.name}
                  className="flex h-28 items-center justify-center rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm transition duration-200 hover:-translate-y-1 hover:border-blue-200 hover:shadow-lg sm:h-32 sm:p-6"
                >
                  <img
                    src={item.logo}
                    alt={item.name}
                    className={`max-h-full max-w-full object-contain ${item.name === 'Watchvault' ? 'scale-125' : ''} ${item.name === 'Klocksalongen' ? 'rounded-md bg-[#80011F] p-1' : ''}`}
                  />
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-8 lg:py-20">
          <div className="rounded-[2rem] border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-sky-50 p-8 sm:p-10 lg:flex lg:items-center lg:justify-between lg:p-12">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-700">
                Tryggare klockaffärer
              </p>
              <h2 className="mt-3 max-w-2xl font-poppins text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                Extra trygghet när du köper och säljer
              </h2>
              <p className="mt-4 max-w-2xl leading-7 text-slate-600">
                Läs mer om våra tilläggstjänster Kronos Certified för äkthetskontroll och Kronos
                Secure för köpskydd.
              </p>
            </div>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row lg:mt-0 lg:shrink-0">
              <Link
                to="/kronos-certified"
                className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-6 py-3.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-blue-200 hover:text-blue-700"
              >
                Kronos Certified
              </Link>
              <Link
                to="/kronos-secure"
                className="inline-flex items-center justify-center rounded-full bg-blue-700 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-700/20 transition hover:bg-blue-800"
              >
                Kronos Secure{' '}
                <span className="ml-2" aria-hidden="true">
                  ↗
                </span>
              </Link>
            </div>
          </div>
        </section>

        <section
          aria-labelledby="buy-sell-heading"
          className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-8 lg:py-20"
        >
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-700">
                Köp och sälj online
              </p>
              <h2
                id="buy-sell-heading"
                className="mt-3 font-poppins text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl"
              >
                Hitta begagnade klockor och sälj klockor på ett och samma ställe
              </h2>
            </div>
            <div className="space-y-4 text-base leading-7 text-slate-600">
              <p>
                Kronos är en svensk marknadsplats för dig som vill köpa eller sälja klockor online.
                Utforska annonser, jämför olika modeller och kontakta säljare direkt i appen.
                Oavsett om du letar efter din första kvalitetsklocka eller vill komplettera en
                samling kan du upptäcka klockor i olika stilar och prisklasser.
              </p>
              <p>
                Söker du en begagnad Rolex, Omega eller en annan klockmodell? Använd Kronos för att
                upptäcka annonser från privatpersoner och professionella handlare. Läs alltid
                annonsinformationen noggrant och ställ frågor till säljaren innan du genomför ett
                köp.
              </p>
              <p>
                För ytterligare stöd kan du läsa om{' '}
                <Link
                  to="/kronos-secure"
                  className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4 hover:text-blue-900"
                >
                  Kronos Secure köpskydd
                </Link>{' '}
                och{' '}
                <Link
                  to="/kronos-certified"
                  className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4 hover:text-blue-900"
                >
                  Kronos Certified äkthetskontroll
                </Link>
                . Du kan även läsa mer om hur du{' '}
                <Link
                  to="/klockvardering"
                  className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4 hover:text-blue-900"
                >
                  värderar din klocka
                </Link>{' '}
                eller hur företag kan{' '}
                <Link
                  to="/foretagsannonsering"
                  className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4 hover:text-blue-900"
                >
                  annonsera på Kronos
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        <section
          aria-labelledby="faq-heading"
          className="border-y border-slate-200 bg-slate-50 py-16 sm:py-20"
        >
          <div className="mx-auto max-w-4xl px-5 sm:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-700">
                Vanliga frågor
              </p>
              <h2
                id="faq-heading"
                className="mt-3 font-poppins text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl"
              >
                Frågor om att köpa och sälja klockor
              </h2>
            </div>
            <div className="mt-10 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white px-6 sm:px-8">
              <article className="py-6">
                <h3 className="text-lg font-semibold text-slate-950">Vad är Kronos?</h3>
                <p className="mt-2 leading-7 text-slate-600">
                  Kronos är en svensk marknadsplats för klockor där användare kan upptäcka annonser,
                  köpa och sälja klockor samt kommunicera direkt i appen.
                </p>
              </article>
              <article className="py-6">
                <h3 className="text-lg font-semibold text-slate-950">
                  Kan jag köpa begagnade klockor på Kronos?
                </h3>
                <p className="mt-2 leading-7 text-slate-600">
                  Ja, Kronos samlar klockannonser från privatpersoner och handlare. Läs annonsen,
                  jämför informationen och kontakta säljaren om du vill veta mer om en specifik
                  klocka.
                </p>
              </article>
              <article className="py-6">
                <h3 className="text-lg font-semibold text-slate-950">
                  Hur kan jag göra en klockaffär tryggare?
                </h3>
                <p className="mt-2 leading-7 text-slate-600">
                  Läs om{' '}
                  <Link
                    to="/kronos-secure"
                    className="font-semibold text-blue-700 underline underline-offset-4"
                  >
                    Kronos Secure köpskydd
                  </Link>{' '}
                  och{' '}
                  <Link
                    to="/kronos-certified"
                    className="font-semibold text-blue-700 underline underline-offset-4"
                  >
                    Kronos Certified
                  </Link>{' '}
                  för att förstå vilka tilläggstjänster som finns och vad de omfattar.
                </p>
              </article>
              <article className="py-6">
                <h3 className="text-lg font-semibold text-slate-950">Hur säljer jag min klocka?</h3>
                <p className="mt-2 leading-7 text-slate-600">
                  Ladda ner Kronos, skapa en annons med tydliga bilder och relevant information och
                  svara på frågor från intresserade köpare.
                </p>
              </article>
              <article className="py-6">
                <h3 className="text-lg font-semibold text-slate-950">
                  Var kan jag få min klocka servad eller reparerad?
                </h3>
                <p className="mt-2 leading-7 text-slate-600">
                  Behöver din klocka service eller reparation? Vi på Kronos samarbetar med UrBrev,
                  som erbjuder professionell klockservice och reparationer. Läs mer om deras
                  tjänster på{' '}
                  <a
                    href="https://urbrev.se"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-blue-700 underline underline-offset-4"
                  >
                    UrBrevs webbplats
                  </a>
                  .
                </p>
              </article>
              <article className="py-6">
                <h3 className="text-lg font-semibold text-slate-950">
                  Var kan jag få min klocka värderad?
                </h3>
                <p className="mt-2 leading-7 text-slate-600">
                  Läs mer om tjänsten{' '}
                  <Link
                    to="/klockvardering"
                    className="font-semibold text-blue-700 underline underline-offset-4"
                  >
                    Klockvärdering
                  </Link>{' '}
                  för information om värdering av klockor.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="relative isolate overflow-hidden bg-slate-950 px-5 py-16 text-center sm:px-8 sm:py-20">
          <div className="absolute -right-20 -top-32 -z-10 h-96 w-96 rounded-full bg-blue-600/30 blur-3xl" />
          <div className="absolute -bottom-40 -left-20 -z-10 h-96 w-96 rounded-full bg-sky-500/20 blur-3xl" />
          <div className="mx-auto max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-300">
              Klockintresset samlat på en plats
            </p>
            <h2 className="mt-4 font-poppins text-4xl font-semibold tracking-tight text-white sm:text-5xl">
              Hitta din nästa klocka på Kronos
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-300">
              Ladda ner appen och upptäck en marknadsplats byggd för passionen för klockor.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a href="https://apps.apple.com/se/app/chronos/id6472299482">
                <img src={applestore} alt="Ladda ner på App Store" className="h-12 w-auto" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.stagem.Chronos">
                <img src={googleplay} alt="Hämta på Google Play" className="h-12 w-auto" />
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

export default StartPage
